import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiClock,
  FiLoader,
  FiCheck,
  FiSave,
  FiX,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  productInstanceIds,
  productUnlocksConsulting,
} from "../utils/mintSpaceApps";
import { CONSULTING_TIMEZONES } from "../constants/consultingTimezones";
import {
  getConsultingCalendarSettings,
  saveConsultingCalendarSettings,
  getConsultingCalendarMonth,
  getConsultingCalendarDaySlots,
  listConsultingBookings,
  createConsultingBooking,
  cancelConsultingBooking,
  addConsultingOneOffSlot,
} from "../api/mintSpaceConsulting";
import ConsultingDateTimePicker from "./ConsultingDateTimePicker";
import { PopUpSelect } from "./ds/PopUpButton";

const WEEK_DAYS = [
  { id: 0, short: "Su", label: "Sunday" },
  { id: 1, short: "Mo", label: "Monday" },
  { id: 2, short: "Tu", label: "Tuesday" },
  { id: 3, short: "We", label: "Wednesday" },
  { id: 4, short: "Th", label: "Thursday" },
  { id: 5, short: "Fr", label: "Friday" },
  { id: 6, short: "Sa", label: "Saturday" },
];

const defaultWeekly = () =>
  [1, 2, 3, 4, 5].map((day) => ({ day, start: "10:00", end: "18:00", on: true }));

const mergeWeeklyUi = (weekly) => {
  const map = new Map((weekly || []).map((w) => [w.day, w]));
  return WEEK_DAYS.map(({ id }) => {
    const w = map.get(id);
    return {
      day: id,
      on: !!w,
      start: w?.start || "10:00",
      end: w?.end || "18:00",
    };
  });
};

const weeklyToPayload = (rows) =>
  rows
    .filter((r) => r.on)
    .map(({ day, start, end }) => ({ day, start, end }));

export default function ConsultingCalendarPanel({
  isDark,
  setToast,
  mintSpace,
  appInstanceId,
  isAdmin,
  engagements = [],
  initialProductId,
  onBooked,
}) {
  const { t } = useTranslation();
  const mintSpaceId = mintSpace?._id;

  const offerings = useMemo(() => {
    if (!mintSpace?.appPricing?.length || !appInstanceId) return [];
    const inst = String(appInstanceId);
    return mintSpace.appPricing.filter(
      (p) =>
        productUnlocksConsulting(p) &&
        productInstanceIds(p, mintSpace).some((id) => String(id) === inst)
    );
  }, [mintSpace, appInstanceId]);

  const [productId, setProductId] = useState(initialProductId || offerings[0]?._id || "");
  const [viewMonth, setViewMonth] = useState(() => DateTime.now().toFormat("yyyy-MM"));
  const [selectedDay, setSelectedDay] = useState(() => DateTime.now().toFormat("yyyy-MM-dd"));
  const [daysWithSlots, setDaysWithSlots] = useState([]);
  const [timezone, setTimezone] = useState("Asia/Tbilisi");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [weeklyUi, setWeeklyUi] = useState(defaultWeekly);
  const [slotMinutes, setSlotMinutes] = useState(45);
  const [bufferMinutes, setBufferMinutes] = useState(10);
  const [minNoticeHours, setMinNoticeHours] = useState(3);
  const [calendarEnabled, setCalendarEnabled] = useState(true);
  const [oneOffStartUtc, setOneOffStartUtc] = useState(null);
  const [oneOffMinutes, setOneOffMinutes] = useState("45");
  const [addingOneOff, setAddingOneOff] = useState(false);

  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const glass = isDark
    ? "border-transparent bg-bg-raised"
    : "border-transparent bg-bg-raised";
  const slotChip = (active) =>
    active
      ? "border-transparent bg-app-consulting-base text-app-consulting-on"
      : isDark
        ? "border-white/10 bg-white/[0.04] text-slate-200 hover:border-app-consulting-base hover:bg-app-consulting-deep"
        : "border-slate-200 bg-white text-slate-800 hover:border-app-consulting-base hover:bg-app-consulting-deep";

  const engagementForProduct = useMemo(
    () => engagements.find((e) => String(e.product) === String(productId) && e.status === "active"),
    [engagements, productId]
  );

  useEffect(() => {
    if (initialProductId) setProductId(initialProductId);
    else if (!productId && offerings[0]?._id) setProductId(offerings[0]._id);
  }, [initialProductId, offerings, productId]);

  const loadMonth = useCallback(async () => {
    if (!mintSpaceId || !productId) return;
    try {
      const res = await getConsultingCalendarMonth(mintSpaceId, productId, viewMonth);
      if (res.success) {
        setDaysWithSlots(res.data.daysWithSlots || []);
        setTimezone(res.data.timezone || "Asia/Tbilisi");
      }
    } catch {
      /* optional */
    }
  }, [mintSpaceId, productId, viewMonth]);

  const loadSlots = useCallback(async () => {
    if (!mintSpaceId || !productId || !selectedDay) return;
    setSlotsLoading(true);
    try {
      const res = await getConsultingCalendarDaySlots(mintSpaceId, productId, selectedDay);
      if (res.success) {
        setSlots(res.data.slots || []);
        setTimezone(res.data.timezone || timezone);
      }
    } catch (err) {
      setToast?.(err?.message || "Could not load times");
    } finally {
      setSlotsLoading(false);
    }
  }, [mintSpaceId, productId, selectedDay, setToast, timezone]);

  const loadBookings = useCallback(async () => {
    if (!mintSpaceId || !appInstanceId) return;
    try {
      const res = await listConsultingBookings(mintSpaceId, {
        instanceId: appInstanceId,
        productId: productId || undefined,
        from: new Date().toISOString(),
      });
      if (res.success) setBookings(res.data.bookings || []);
    } catch {
      /* optional */
    }
  }, [mintSpaceId, appInstanceId, productId]);

  const loadSettings = useCallback(async () => {
    if (!isAdmin || !mintSpaceId || !productId) return;
    setSettingsLoading(true);
    try {
      const res = await getConsultingCalendarSettings(mintSpaceId, productId);
      if (res.success) {
        const s = res.data.settings;
        setCalendarEnabled(s.enabled !== false);
        setTimezone(s.timezone || "Asia/Tbilisi");
        setWeeklyUi(mergeWeeklyUi(s.weekly));
        setSlotMinutes(s.slotMinutes || 45);
        setBufferMinutes(s.bufferMinutes ?? 10);
        setMinNoticeHours(s.minNoticeHours ?? 3);
      }
    } catch (err) {
      setToast?.(err?.message || "Could not load availability");
    } finally {
      setSettingsLoading(false);
    }
  }, [isAdmin, mintSpaceId, productId, setToast]);

  useEffect(() => {
    loadMonth();
    loadBookings();
  }, [loadMonth, loadBookings]);

  useEffect(() => {
    loadSlots();
    setSelectedSlot(null);
  }, [loadSlots]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!isAdmin || oneOffStartUtc) return;
    const t0 = DateTime.now()
      .setZone(timezone)
      .plus({ days: 1 })
      .set({ hour: 10, minute: 0, second: 0, millisecond: 0 });
    setOneOffStartUtc(t0.toUTC().toISO());
  }, [isAdmin, timezone, oneOffStartUtc]);

  const handleAddOneOff = async () => {
    if (!mintSpaceId || !productId || !oneOffStartUtc) return;
    setAddingOneOff(true);
    try {
      const start = DateTime.fromISO(oneOffStartUtc, { zone: "utc" });
      const mins = Math.max(15, Number(oneOffMinutes) || 45);
      const end = start.plus({ minutes: mins });
      await addConsultingOneOffSlot(
        mintSpaceId,
        productId,
        { startAt: start.toISO(), endAt: end.toISO() },
        appInstanceId
      );
      setToast?.(t("consulting.oneOffAdded") || "Session time added to calendar");
      await loadMonth();
      await loadSlots();
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Failed to add time");
    } finally {
      setAddingOneOff(false);
    }
  };

  const saveSettings = async () => {
    if (!mintSpaceId || !productId) return;
    setSavingSettings(true);
    try {
      const res = await saveConsultingCalendarSettings(
        mintSpaceId,
        productId,
        {
          enabled: calendarEnabled,
          timezone,
          weekly: weeklyToPayload(weeklyUi),
          slotMinutes,
          bufferMinutes,
          minNoticeHours,
        },
        appInstanceId
      );
      if (res.success) {
        setToast?.(t("consulting.calendarSaved") || "Availability saved");
        await loadMonth();
        await loadSlots();
      }
    } catch (err) {
      setToast?.(err?.message || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const monthDt = DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd");
  const monthLabel = monthDt.isValid ? monthDt.toFormat("LLLL yyyy") : viewMonth;

  const calendarCells = useMemo(() => {
    if (!monthDt.isValid) return [];
    const start = monthDt.startOf("month");
    const end = monthDt.endOf("month");
    const padStart = start.weekday === 7 ? 0 : start.weekday;
    const cells = [];
    for (let i = 0; i < padStart; i += 1) cells.push({ empty: true, key: `p-${i}` });
    for (let d = start; d <= end; d = d.plus({ days: 1 })) {
      const iso = d.toFormat("yyyy-MM-dd");
      cells.push({
        empty: false,
        key: iso,
        iso,
        day: d.day,
        isToday: iso === DateTime.now().toFormat("yyyy-MM-dd"),
        hasSlots: daysWithSlots.includes(iso),
        selected: iso === selectedDay,
      });
    }
    return cells;
  }, [monthDt, daysWithSlots, selectedDay]);

  const shiftMonth = (delta) => {
    const next = monthDt.plus({ months: delta }).toFormat("yyyy-MM");
    setViewMonth(next);
  };

  const confirmBook = async () => {
    if (!selectedSlot || !engagementForProduct) return;
    setBookingBusy(true);
    try {
      const res = await createConsultingBooking(mintSpaceId, {
        productId,
        engagementId: engagementForProduct._id,
        startAt: selectedSlot.startAt,
      });
      if (res.success) {
        setToast?.(t("consulting.bookingConfirmed") || "Session booked!");
        setSelectedSlot(null);
        await loadMonth();
        await loadSlots();
        await loadBookings();
        onBooked?.(res.data.booking);
      }
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Booking failed");
      await loadSlots();
    } finally {
      setBookingBusy(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      const res = await cancelConsultingBooking(mintSpaceId, bookingId);
      if (res.success) {
        setToast?.(t("consulting.bookingCancelled") || "Booking cancelled");
        await loadBookings();
        await loadMonth();
        await loadSlots();
      }
    } catch (err) {
      setToast?.(err?.message || "Cancel failed");
    }
  };

  if (!offerings.length) {
    return (
      <div className={`flex flex-1 items-center justify-center p-8 text-subheadline ${muted}`}>
        {t("consulting.calendarNeedOffering") || "Add a consultation offering first."}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      <div className={`flex min-h-0 flex-1 flex-col border-b lg:border-b-0 lg:border-r border-separator-default`}>
        <div className={`shrink-0 border-b px-4 py-3 border-separator-default`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-radius-sm bg-app-consulting-base text-app-consulting-on">
                <FiCalendar className="h-4 w-4" />
              </span>
              <div>
                <p className={`text-subheadline-semibold ${baseText}`}>
                  {t("consulting.calendarTitle") || "Schedule"}
                </p>
                <p className={`text-caption-1 ${muted}`}>{timezone.replace("_", " ")}</p>
              </div>
            </div>
            {offerings.length > 1 && (
              <PopUpSelect
                label={t("consulting.offering")}
                value={productId}
                onChange={setProductId}
                className="ml-auto max-w-[200px]"
              >
                {offerings.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name || "Offering"}
                  </option>
                ))}
              </PopUpSelect>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 campaign-window-scrollbar">
          <div className={`consulting-calendar-card overflow-hidden rounded-radius-md border p-4 shadow-xl ${glass}`}>
            <div className="mb-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                className={`rounded-radius-sm p-2 transition hover:bg-fill-quaternary`}
                aria-label="Previous month"
              >
                <FiChevronLeft className="h-5 w-5" />
              </button>
              <h3 className={`text-headline tracking-tight ${baseText}`}>{monthLabel}</h3>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                className={`rounded-radius-sm p-2 transition hover:bg-fill-quaternary`}
                aria-label="Next month"
              >
                <FiChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className={`mb-2 grid grid-cols-7 gap-1 text-center text-caption-1-semibold uppercase tracking-wider ${muted}`}>
              {WEEK_DAYS.map((d) => (
                <span key={d.id}>{d.short}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1.5">
              {calendarCells.map((cell) =>
                cell.empty ? (
                  <div key={cell.key} className="aspect-square" />
                ) : (
                  <button
                    key={cell.key}
                    type="button"
                    onClick={() => setSelectedDay(cell.iso)}
                    className={`consulting-cal-day relative flex aspect-square flex-col items-center justify-center rounded-radius-sm text-subheadline-semibold transition-all duration-200 ${
                      cell.selected
                        ? "consulting-cal-day-selected scale-[1.02] bg-app-consulting-base text-app-consulting-on"
                        : cell.isToday
                          ? "ring-1 ring-accent-default bg-fill-tertiary text-label-primary"
                          : isDark
                            ? "text-slate-300 hover:bg-white/[0.08]"
                            : "text-slate-700 hover:bg-white"
                    } ${!cell.hasSlots && !cell.selected ? "opacity-45" : ""}`}
                  >
                    {cell.day}
                    {cell.hasSlots && !cell.selected && (
                      <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-app-consulting-deep" />
                    )}
                  </button>
                )
              )}
            </div>
          </div>

          <div className="mt-4">
              <p className={`mb-2 flex items-center gap-1.5 text-footnote-semibold ${baseText}`}>
                <FiClock className="h-3.5 w-3.5 text-app-consulting-soft" />
                {DateTime.fromISO(selectedDay).toFormat("cccc, LLL d")}
              </p>
              {slotsLoading ? (
                <div className={`flex items-center gap-2 py-6 text-subheadline ${muted}`}>
                  <FiLoader className="h-4 w-4 animate-spin" />
                  {t("common.loading") || "Loading…"}
                </div>
              ) : slots.length === 0 ? (
                <p className={`rounded-radius-sm border border-dashed px-4 py-6 text-center text-footnote ${muted} border-separator-default`}>
                  {t("consulting.noSlotsDay") || "No open times this day — try another date."}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {slots.map((s) => (
                    <button
                      key={s.startAt}
                      type="button"
                      onClick={() => setSelectedSlot(s)}
                      className={`rounded-radius-sm border px-3 py-2.5 text-footnote-semibold transition-all duration-150 ${slotChip(selectedSlot?.startAt === s.startAt)}`}
                    >
                      {s.label || s.localStart}
                    </button>
                  ))}
                </div>
              )}

              {selectedSlot && (
                <div
                  className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-radius-md border p-3 ${
                    isDark ? "border-app-consulting-base bg-app-consulting-deep" : "border-app-consulting-base bg-app-consulting-deep"
                  }`}
                >
                  <p className={`text-footnote font-medium ${baseText}`}>
                    {(t("consulting.confirmSlot") || "Book {time}?").replace(
                      "{time}",
                      selectedSlot.label || selectedSlot.localStart
                    )}
                  </p>
                  {!engagementForProduct ? (
                    <p className={`text-caption-1 ${muted}`}>
                      {t("consulting.purchaseToBook") || "Purchase this consultation to book a time."}
                    </p>
                  ) : (
                    <button
                      type="button"
                      disabled={bookingBusy}
                      onClick={confirmBook}
                      className="inline-flex items-center gap-1.5 rounded-radius-sm bg-accent-default px-4 py-2 text-footnote-semibold text-accent-on disabled:opacity-60"
                    >
                      {bookingBusy ? <FiLoader className="h-3.5 w-3.5 animate-spin" /> : <FiCheck className="h-3.5 w-3.5" />}
                      {t("consulting.confirmBook") || "Confirm"}
                    </button>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>

      <aside className={`flex w-full shrink-0 flex-col lg:w-[320px] bg-bg-inset`}>
        {isAdmin ? (
          <>
            <div className={`border-b px-4 py-3 border-separator-default`}>
              <p className={`text-subheadline-semibold ${baseText}`}>
                {t("consulting.addOneOffTitle") || "Add session at date & time"}
              </p>
              <p className={`mt-0.5 text-caption-1 ${muted}`}>
                {t("consulting.addOneOffHint") || "Pick date, hour, minute and second — clients can book this slot."}
              </p>
              <div className="mt-3 space-y-2">
                <ConsultingDateTimePicker
                  isDark={isDark}
                  timezone={timezone}
                  value={oneOffStartUtc}
                  onChange={setOneOffStartUtc}
                  minDate={new Date()}
                />
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className={`text-caption-1 ${muted}`}>{t("consulting.sessionLengthMin") || "Minutes"}</label>
                    <input
                      type="number"
                      min="15"
                      max="180"
                      value={oneOffMinutes}
                      onChange={(e) => setOneOffMinutes(e.target.value)}
                      className={`mt-0.5 w-full rounded-radius-xs border px-2 py-1.5 text-footnote border-separator-default bg-bg-raised`}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={addingOneOff}
                    onClick={handleAddOneOff}
                    className="rounded-radius-sm bg-app-consulting-base px-4 py-2 text-footnote-semibold text-app-consulting-on disabled:opacity-60"
                  >
                    {addingOneOff ? <FiLoader className="h-4 w-4 animate-spin" /> : t("consulting.addSlot") || "Add"}
                  </button>
                </div>
              </div>
            </div>
            <div className={`border-b px-4 py-3 border-separator-default`}>
              <p className={`text-subheadline-semibold ${baseText}`}>
                {t("consulting.availabilityTitle") || "Your availability"}
              </p>
              <p className={`mt-0.5 text-caption-1 leading-snug ${muted}`}>
                {t("consulting.availabilityHint") ||
                  "Set weekly hours — clients only see open slots that match your session length."}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 campaign-window-scrollbar">
              {settingsLoading ? (
                <FiLoader className={`mx-auto h-6 w-6 animate-spin ${muted}`} />
              ) : (
                <>
                  <label className={`mb-3 flex cursor-pointer items-center gap-2 text-footnote font-medium ${baseText}`}>
                    <input
                      type="checkbox"
                      checked={calendarEnabled}
                      onChange={(e) => setCalendarEnabled(e.target.checked)}
                    />
                    {t("consulting.calendarEnabled") || "Accept bookings"}
                  </label>
                  <div className="mb-3">
                    <p className={`mb-1 text-caption-1-semibold uppercase tracking-wide ${muted}`}>
                      {t("consulting.timezone") || "Timezone"}
                    </p>
                    <PopUpSelect label={t("consulting.timezone")} value={timezone} onChange={setTimezone} className="w-full">
                      {CONSULTING_TIMEZONES.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.label}
                        </option>
                      ))}
                    </PopUpSelect>
                  </div>
                  <div className="mb-3 grid grid-cols-3 gap-2">
                    {[
                      { key: "slotMinutes", label: t("consulting.slotLength") || "Session", value: slotMinutes, set: setSlotMinutes, opts: [30, 45, 60] },
                      { key: "buffer", label: t("consulting.buffer") || "Buffer", value: bufferMinutes, set: setBufferMinutes, opts: [0, 10, 15] },
                      { key: "notice", label: t("consulting.minNotice") || "Notice (h)", value: minNoticeHours, set: setMinNoticeHours, opts: [1, 3, 24] },
                    ].map((row) => (
                      <div key={row.key}>
                        <p className={`mb-1 text-caption-1-semibold uppercase tracking-wide ${muted}`}>{row.label}</p>
                        <PopUpSelect label={row.label} value={row.value} onChange={(value) => row.set(Number(value))} className="w-full">
                          {row.opts.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </PopUpSelect>
                      </div>
                    ))}
                  </div>
                  <ul className="space-y-2">
                    {weeklyUi.map((row) => (
                      <li
                        key={row.day}
                        className={`rounded-radius-sm border p-2.5 border-separator-default bg-bg-raised`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={row.on}
                            onChange={(e) =>
                              setWeeklyUi((prev) =>
                                prev.map((r) => (r.day === row.day ? { ...r, on: e.target.checked } : r))
                              )
                            }
                          />
                          <span className={`w-8 text-footnote-semibold ${baseText}`}>
                            {WEEK_DAYS.find((d) => d.id === row.day)?.short}
                          </span>
                          <input
                            type="time"
                            value={row.start}
                            disabled={!row.on}
                            onChange={(e) =>
                              setWeeklyUi((prev) =>
                                prev.map((r) => (r.day === row.day ? { ...r, start: e.target.value } : r))
                              )
                            }
                            className={`min-w-0 flex-1 rounded-radius-xs border px-1.5 py-1 text-caption-1 disabled:opacity-40 ${isDark ? "border-white/10 bg-black/20" : "border-slate-200"}`}
                          />
                          <span className={`text-caption-1 ${muted}`}>–</span>
                          <input
                            type="time"
                            value={row.end}
                            disabled={!row.on}
                            onChange={(e) =>
                              setWeeklyUi((prev) =>
                                prev.map((r) => (r.day === row.day ? { ...r, end: e.target.value } : r))
                              )
                            }
                            className={`min-w-0 flex-1 rounded-radius-xs border px-1.5 py-1 text-caption-1 disabled:opacity-40 ${isDark ? "border-white/10 bg-black/20" : "border-slate-200"}`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    disabled={savingSettings}
                    onClick={saveSettings}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-radius-sm bg-app-consulting-base py-2.5 text-footnote-semibold text-app-consulting-on disabled:opacity-60"
                  >
                    {savingSettings ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSave className="h-4 w-4" />}
                    {t("consulting.saveAvailability") || "Save availability"}
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-4">
            <p className={`text-subheadline-semibold ${baseText}`}>
              {t("consulting.upcomingBookings") || "Upcoming"}
            </p>
            <ul className="mt-3 space-y-2">
              {bookings.length === 0 && (
                <li className={`text-footnote ${muted}`}>{t("consulting.noUpcoming") || "No sessions scheduled yet."}</li>
              )}
              {bookings.map((b) => (
                <li
                  key={b._id}
                  className={`rounded-radius-sm border p-3 text-footnote border-separator-default bg-bg-raised`}
                >
                  <p className={`font-semibold ${baseText}`}>{b.label}</p>
                  <p className={`mt-0.5 ${muted}`}>{b.productName}</p>
                  {b.status === "scheduled" && (
                    <button
                      type="button"
                      onClick={() => handleCancelBooking(b._id)}
                      className={`mt-2 inline-flex items-center gap-1 text-caption-1-semibold text-status-danger hover:underline`}
                    >
                      <FiX className="h-3 w-3" />
                      {t("consulting.cancelBooking") || "Cancel"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isAdmin && bookings.length > 0 && (
          <div className={`border-t p-4 border-separator-default`}>
            <p className={`mb-2 text-footnote-semibold ${baseText}`}>
              {t("consulting.upcomingBookings") || "Upcoming"}
            </p>
            <ul className="max-h-40 space-y-2 overflow-y-auto campaign-window-scrollbar">
              {bookings.slice(0, 8).map((b) => (
                <li key={b._id} className={`rounded-radius-xs px-2 py-1.5 text-caption-1 bg-fill-tertiary`}>
                  <span className={baseText}>{b.label}</span>
                  <span className={`ml-1 ${muted}`}>· {b.productName}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}
